import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  demoMessages, 
  demoResponses, 
  initialResponses, 
  responseTemplates, 
  mockResponseTemplates, 
  mockData,
  formatResponseFromTemplate
} from './content/WhatsAppMessages';

interface Message {
  id: number;
  sender: 'user' | 'agent';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
  toolCalls?: ToolCall[];
  status?: 'sent' | 'delivered' | 'read';
}

interface ToolCall {
  tool: string;
  input?: any;
  output?: any;
  status: 'running' | 'complete' | 'error';
}

interface WhatsAppInterfaceProps {
  initialMessages?: Message[];
  onSendMessage: (message: string) => void;
  autoDemo?: boolean;
}

// Unused interface commented out
// interface SuggestionButton {
//   text: string;
//   action: string;
// }

const WhatsAppInterface: React.FC<WhatsAppInterfaceProps> = ({ initialMessages = [], onSendMessage, autoDemo = true }) => {
  const [messages, setMessages] = useState<Message[]>(initialMessages);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [demoComplete, setDemoComplete] = useState(false);
  const [demoInProgress, setDemoInProgress] = useState(false);
  const demoRunningRef = useRef(false);
  const demoLoopCountRef = useRef(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const messagesRef = useRef<Message[]>(messages);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Keep messagesRef updated with latest messages
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  // Sample chats for WhatsApp sidebar
  const chats = [
    { name: 'AgentBot', message: 'I can help with that!', time: '9:45 AM', unread: 0, pinned: true },
    { name: 'Family Group', message: 'Mom: We\'ll meet at 7', time: '8:30 AM', unread: 3, pinned: false },
    { name: 'Work Team', message: 'Alice: Meeting in 10 minutes', time: 'Yesterday', unread: 0, pinned: false },
    { name: 'David Johnson', message: 'Thanks for the update', time: 'Yesterday', unread: 0, pinned: false },
    { name: 'Sarah Williams', message: 'Let\'s catch up soon!', time: '3/15/24', unread: 0, pinned: false },
  ];
  
  // Suggestion buttons shown in the UI (empty for now) - commented out to fix unused variable warning
  // const suggestionButtons: SuggestionButton[] = [];

  // Fisher-Yates shuffle algorithm for randomizing message order
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  // Auto demo functionality
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoDemo && demoMessages.length > 0 && !demoComplete && !demoInProgress && !demoRunningRef.current && (messages.length === 0 || demoLoopCountRef.current > 0)) {
      setDemoInProgress(true);
      demoRunningRef.current = true;
      runDemoSequence();
    }
  }, [autoDemo, messages.length, demoComplete, demoInProgress]);
  
  // Disabled exhaustive-deps for this effect because runDemoSequence would create a circular reference

  // Update time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (chatContainerRef.current) {
      // Use scrollTop to control scroll position within container only
      const scrollHeight = chatContainerRef.current.scrollHeight;
      chatContainerRef.current.scrollTop = scrollHeight;
    }
  };

  // Function to simulate typing and sending a message
  const simulateTyping = (message: string) => {
    return new Promise<void>((resolve) => {
      // Clear any existing text
      setInputValue('');
      
      // Simulate typing character by character
      let currentCharIndex = 0;
      const typingInterval = setInterval(() => {
        if (currentCharIndex <= message.length) {
          setInputValue(message.substring(0, currentCharIndex));
          currentCharIndex++;
        } else {
          clearInterval(typingInterval);
          
          // Small pause after typing before sending
          setTimeout(() => {
            // Call a specific version of send that uses the current inputValue
            handleSendDemoMessage(message);
            resolve();
          }, 500);
        }
      }, 10 + Math.random() * 10); // Fast typing speed for demos
    });
  };

  // Special version of handleSendMessage specifically for the demo
  const handleSendDemoMessage = (demoMessage: string) => {
    if (!demoMessage.trim()) return;
    
    // Create a unique message ID to prevent duplicates
    const messageId = Date.now();
    
    // Create and add the user message
    const userMessage: Message = {
      id: messageId,
      sender: 'user',
      text: demoMessage,
      timestamp: new Date(),
      status: 'sent'
    };
    
    // Only add this message if it doesn't already exist
    setMessages(prev => {
      // Check if this message already exists
      const exists = prev.some(m => m.text === demoMessage && m.sender === 'user');
      if (exists) return prev;
      return [...prev, userMessage];
    });
    
    setInputValue('');
    
    // Simulate message delivery status updates
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated.find(m => m.id === messageId);
        if (lastUserMessage) {
          lastUserMessage.status = 'delivered';
        }
        return [...updated];
      });
    }, 500);
    
    // Simulate agent responding with the predetermined response for this demo message
    setTimeout(() => {
      // Update status to read
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated.find(m => m.id === messageId);
        if (lastUserMessage) {
          lastUserMessage.status = 'read';
        }
        return [...updated];
      });
      
      setIsTyping(true);
    }, 1000);
    
    // Simulate agent processing time
    const processingTime = 1000 + Math.random() * 1000;
    
    setTimeout(() => {
      setIsTyping(false);
      
      // Use predefined responses for the demo
      const demoResponse = demoResponses[demoMessage];
      if (!demoResponse) return;
      
      const initialResponseText = initialResponses[demoResponse.tool] || "Let me look into that for you...";
      
      const responseId = Date.now() + 1;
      const botResponse: Message = {
        id: responseId,
        sender: 'agent',
        text: initialResponseText,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: demoResponse.tool,
            input: demoResponse.input,
            status: 'running',
          }
        ]
      };
      
      // Only add this message if the user message exists and this bot response doesn't
      setMessages(prev => {
        // Check if we already have this response 
        const userExists = prev.some(m => m.text === demoMessage && m.sender === 'user');
        const botExists = prev.some(m => 
          m.sender === 'agent' && 
          m.text === initialResponseText &&
          m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
        );
        
        if (!userExists || botExists) return prev;
        return [...prev, botResponse];
      });
      
      scrollToBottom();
      
      // Tools always run and complete successfully
      const toolCompletionTime = 1500 + Math.random() * 1000;
      
      setTimeout(() => {
        setMessages(prev => {
          const updatedMessages = [...prev];
          // Find the exact message by comparing content rather than just ID
          const lastMessage = updatedMessages.find(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText &&
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool)
          );
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = demoResponse.output;
            
            // Use the helper function to format the response
            const formattedResponse = formatResponseFromTemplate(
              responseTemplates[toolCall.tool] || "I've completed your request. Here are the results.",
              demoResponse.output,
              toolCall.tool
            );
            
            // Update the response text
            lastMessage.text = formattedResponse;
          }
          
          return updatedMessages.map(m => 
            m.sender === 'agent' && 
            m.text === initialResponseText && 
            m.toolCalls?.some(tc => tc.tool === demoResponse.tool) && 
            lastMessage ? lastMessage : m
          );
        });
        
        scrollToBottom();
      }, toolCompletionTime);
    }, processingTime);
  };

  // Run the full demo sequence - wrapped in useCallback to avoid lint errors
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const runDemoSequence = useCallback(async () => {
    // Increment loop counter
    demoLoopCountRef.current++;
    console.log(`Starting WhatsApp demo loop ${demoLoopCountRef.current}/1`);
    
    // Wait for initial render
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Randomize the order of messages for this loop
    const randomizedMessages = shuffleArray(demoMessages);
    
    // Loop through each demo message
    for (let i = 0; i < randomizedMessages.length; i++) {
      // Wait for previous message to be processed
      if (i > 0) {
        // Wait for agent to finish responding to previous message
        await new Promise(resolve => {
          const checkInterval = setInterval(() => {
            // Use messagesRef to get the latest messages
            const currentMessages = messagesRef.current;
            const lastMessage = currentMessages[currentMessages.length - 1];
            
            if (lastMessage && lastMessage.sender === 'agent' && 
                (!lastMessage.toolCalls || 
                 lastMessage.toolCalls.every(tc => tc.status === 'complete'))) {
              clearInterval(checkInterval);
              // Add some delay between conversations
              setTimeout(resolve, 2000);
            }
          }, 500);
        });
      }
      
      // Type and send the next message
      await simulateTyping(randomizedMessages[i]);
    }
    
    // Only run a single loop, then mark as complete
    demoRunningRef.current = false;
    setDemoInProgress(false);
    setDemoComplete(true);
    
    // Switch to the next platform (Gmail)
    const switchPlatformEvent = new CustomEvent('switchPlatform', {
      detail: { platform: 'gmail' }
    });
    window.dispatchEvent(switchPlatformEvent);
  }, []);

  const handleSendMessage = useCallback((text = inputValue) => {
    if (text.trim() === '') return;

    // Add user message
    const newMessage: Message = {
      id: Date.now(),
      sender: 'user',
      text: text,
      timestamp: new Date(),
      status: 'sent'
    };

    setMessages(prev => [...prev, newMessage]);
    onSendMessage(text);
    setInputValue('');

    // Update message status after a delay (sent -> delivered -> read)
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated[updated.length - 1];
        if (lastUserMessage.sender === 'user') {
          lastUserMessage.status = 'delivered';
        }
        return updated;
      });
    }, 1000);

    // Simulate agent typing
    setIsTyping(true);
    setTimeout(() => {
      setMessages(prev => {
        const updated = [...prev];
        const lastUserMessage = updated[updated.length - 1];
        if (lastUserMessage.sender === 'user') {
          lastUserMessage.status = 'read';
        }
        return updated;
      });

      setIsTyping(false);
      
      // Randomly select a WhatsApp-appropriate tool
      const whatsappTools = ['language_learning', 'recipe_search', 'movie_info', 'fitness_planner', 'book_recommendations'];
      const selectedTool = whatsappTools[Math.floor(Math.random() * whatsappTools.length)];
      
      // Get data from the mockData in WhatsAppMessages.tsx
      const initialResponse = initialResponses[selectedTool] || "I'll help with that!";
      const mockInput = mockData[selectedTool]?.input || {};
      const mockOutput = mockData[selectedTool]?.output || {};
      
      // This would normally come from your API
      const botResponse: Message = {
        id: Date.now() + 1,
        sender: 'agent',
        text: initialResponse,
        timestamp: new Date(),
        toolCalls: [
          {
            tool: selectedTool,
            input: mockInput,
            status: 'running'
          }
        ]
      };
      
      setMessages(prev => [...prev, botResponse]);
      
      // Simulate tool call completing
      setTimeout(() => {
        setMessages(prev => {
          const updated = [...prev];
          const lastMessage = updated[updated.length - 1];
          
          if (lastMessage?.toolCalls && lastMessage.toolCalls.length > 0) {
            const toolCall = lastMessage.toolCalls[0];
            toolCall.status = 'complete';
            toolCall.output = mockOutput;
            
            // Use the helper function to format the response
            const formattedResponse = formatResponseFromTemplate(
              mockResponseTemplates[toolCall.tool] || "I've completed your request. Here are the results.",
              mockOutput,
              toolCall.tool
            );
            
            // Update the response text
            lastMessage.text = formattedResponse;
          }
          
          return updated;
        });
      }, 2000);
    }, 2500);
  }, [inputValue, onSendMessage]);

  // Unused function commented out
  // const handleSuggestionClick = (text: string) => {
  //   handleSendMessage(text);
  // };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // Handle Enter key to send message
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSendMessage();
    }
  };

  const formatTimestamp = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatPhoneTime = () => {
    return currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const statusIcon = (status?: 'sent' | 'delivered' | 'read') => {
    if (status === 'sent') {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      );
    } else if (status === 'delivered') {
      return (
        <svg className="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
          <path d="M20 6L8.5 17.5l-4-4" />
        </svg>
      );
    } else if (status === 'read') {
      return (
        <svg className="w-3.5 h-3.5 text-blue-500" fill="currentColor" viewBox="0 0 24 24">
          <path d="M4.5 12.75l6 6 9-13.5" />
          <path d="M20 6L8.5 17.5l-4-4" />
        </svg>
      );
    }
    return null;
  };

  // Unused function commented out
  // const toggleSidebar = () => {
  //   setShowSidebar(!showSidebar);
  // };

  return (
    <div className="flex justify-center mx-auto">
      {/* Phone frame */}
      <div className="relative w-[380px] h-[700px] bg-black rounded-[40px] p-3 shadow-xl flex flex-col overflow-hidden">
        {/* Phone notch */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-[150px] h-[30px] bg-black rounded-b-[20px] z-20 flex justify-center items-end">
          <div className="w-[80px] h-[10px] bg-[#333] rounded-[5px] mb-1"></div>
        </div>
        
        {/* Phone content */}
        <div className="flex-1 bg-[#121212] rounded-[32px] overflow-hidden flex flex-col">
          {/* iOS Status bar */}
          <div className="bg-black px-5 py-1 flex items-center justify-between text-white text-sm">
            <div>{formatPhoneTime()}</div>
            <div className="flex items-center space-x-1">
              <div className="flex space-x-px">
                <div className="h-3 w-[5px] bg-white rounded-sm"></div>
                <div className="h-2 w-[5px] bg-white rounded-sm mt-1"></div>
                <div className="h-1.5 w-[5px] bg-white rounded-sm mt-1.5"></div>
                <div className="h-1 w-[5px] bg-white rounded-sm mt-2"></div>
              </div>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z" />
              </svg>
              <div className="font-semibold">53%</div>
            </div>
          </div>
          
          {/* WhatsApp header */}
          <div className="bg-[#1F2C34] px-3 py-2 flex items-center justify-between text-white">
            <div className="flex items-center">
              <button className="mr-2">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 11H7.83L13.42 5.41L12 4L4 12L12 20L13.41 18.59L7.83 13H20V11Z" fill="currentColor"/>
                </svg>
              </button>
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full overflow-hidden mr-2">
                  <img 
                    src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48bGluZWFyR3JhZGllbnQgaWQ9ImEiIHgxPSIwJSIgeTE9IjAlIiB4Mj0iMTAwJSIgeTI9IjEwMCUiPjxzdG9wIHN0b3AtY29sb3I9IiM0Mjg4ZjkiIG9mZnNldD0iMCUiLz48c3RvcCBzdG9wLWNvbG9yPSIjMDA2NmVhIiBvZmZzZXQ9IjEwMCUiLz48L2xpbmVhckdyYWRpZW50PjwvZGVmcz48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxjaXJjbGUgZmlsbD0idXJsKCNhKSIgY3g9IjUwIiBjeT0iNTAiIHI9IjUwIi8+PHBhdGggZD0iTTM1IDQ3LjVDMzUgNTAuNTM4IDM3LjQ2MiA1MyA0MC41IDUzYzMuMDM4IDAgNS41LTIuNDYyIDUuNS01LjVTNDMuNTM4IDQyIDQwLjUgNDJTMzUgNDQuNDYyIDM1IDQ3LjV6bTI1IDBjMCAzLjAzOCAyLjQ2MiA1LjUgNS41IDUuNSAzLjAzOCAwIDUuNS0yLjQ2MiA1LjUtNS41UzY5LjAzOCA0MiA2NiA0MnMtNiAyLjQ2Mi02IDUuNXpNMzMgNjVzNi41LTkgMTctOSAxNyA5IDE3IDkgMS41IDItMiA0Yy0zLjUgMi0xMCA1LTE1IDVzLTExLjUtMy0xNS01Yy0zLjUtMi0yLTQtMi00eiIgZmlsbD0iI0ZGRiIvPjwvZz48L3N2Zz4=" 
                    alt="AI Assistant Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
                <div>
                  <h2 className="font-medium text-xs">AI Assistant</h2>
                  <p className="text-[9px] text-gray-400">Online</p>
                </div>
              </div>
            </div>
            <div className="flex space-x-5 text-white">
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M19.23 15.26L16.69 14.97C16.08 14.9 15.48 15.11 15.05 15.54L13.21 17.38C10.38 15.94 8.06 13.63 6.62 10.79L8.47 8.94C8.9 8.51 9.11 7.91 9.04 7.3L8.75 4.78C8.63 3.77 7.78 3.01 6.76 3.01H5.03C3.9 3.01 2.96 3.95 3.03 5.08C3.56 13.62 10.39 20.44 18.92 20.97C20.05 21.04 20.99 20.1 20.99 18.97V17.24C21 16.23 20.24 15.38 19.23 15.26Z" fill="currentColor"/>
                </svg>
              </button>
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM13 17H11V11H13V17ZM13 9H11V7H13V9Z" fill="currentColor"/>
                </svg>
              </button>
              <button>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8C13.1 8 14 7.1 14 6C14 4.9 13.1 4 12 4C10.9 4 10 4.9 10 6C10 7.1 10.9 8 12 8ZM12 10C10.9 10 10 10.9 10 12C10 13.1 10.9 14 12 14C13.1 14 14 13.1 14 12C14 10.9 13.1 10 12 10ZM12 16C10.9 16 10 16.9 10 18C10 19.1 10.9 20 12 20C13.1 20 14 19.1 14 18C14 16.9 13.1 16 12 16Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
          </div>

          {/* WhatsApp sidebar */}
          <AnimatePresence>
            {showSidebar && (
              <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                transition={{ duration: 0.2 }}
                className="absolute top-24 left-0 right-0 bg-white z-10 shadow-md border-b border-gray-200 max-h-[300px] overflow-y-auto rounded-b-lg"
              >
                <div className="py-2">
                  {chats.map((chat, index) => (
                    <div 
                      key={index}
                      className={`flex items-center p-3 border-b border-gray-100 hover:bg-[#f0f2f5] cursor-pointer ${chat.name === 'AgentBot' ? 'bg-[#f0f2f5]' : ''}`}
                      onClick={() => setShowSidebar(false)}
                    >
                      <div className="w-8 h-8 rounded-full bg-[#25D366] mr-3 flex items-center justify-center text-white font-medium overflow-hidden text-xs">
                        {chat.name === 'AgentBot' ? 'AB' : chat.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-center">
                          <h3 className="font-medium truncate text-[#111b21] text-xs">{chat.name}</h3>
                          <span className="text-[10px] text-gray-500">{chat.time}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <p className="text-xs text-gray-500 truncate">{chat.message}</p>
                          {chat.unread > 0 && (
                            <span className="w-4 h-4 bg-[#25D366] text-white text-[10px] rounded-full flex items-center justify-center">
                              {chat.unread}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* AI disclaimer banner */}
          <div className="bg-[#1F2C34] border-t border-gray-700 px-4 py-2">
            <div className="text-center text-[10px] text-white opacity-70">
              <p>Today</p>
            </div>
          </div>
          
          {/* Chat background with dark pattern */}
          <div 
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto p-2 bg-[#0B141A] scroll-smooth" 
            style={{ 
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23222e35' fill-opacity='0.3' fill-rule='evenodd'/%3E%3C/svg%3E")`,
              overscrollBehavior: 'contain' // Prevent scroll chaining to parent
            }}
            onScroll={(e) => {
              // Prevent scroll propagation to parent
              e.stopPropagation();
            }}
          >
            <div className="space-y-1">
              {/* First welcome message */}
              <div className="flex justify-start">
                <div className="max-w-[85%] relative rounded-lg p-2 bg-[#222E35] shadow-sm text-white">
                  <div className="text-[11px] whitespace-pre-wrap">Hello! I'm your AI assistant. How can I help you today?</div>
                  <div className="flex justify-end items-center mt-1 space-x-1">
                    <span className="text-[9px] text-gray-400">9:47 PM</span>
                  </div>
                  <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#222E35]"></div>
                </div>
              </div>
              
              {messages.map((message) => {
                const isUser = message.sender === 'user';
                return (
                  <div 
                    key={message.id} 
                    className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div 
                      className={`relative max-w-[85%] rounded-lg p-2 ${isUser ? 'bg-[#005D4B]' : 'bg-[#222E35]'} shadow-sm text-white`}
                    >
                      <div className="text-[11px] whitespace-pre-wrap">{message.text}</div>
                      <div className="flex justify-end items-center mt-1 space-x-1">
                        <span className="text-[9px] text-gray-400">
                          {formatTimestamp(message.timestamp)}
                        </span>
                        {isUser && statusIcon(message.status)}
                      </div>
                      
                      {/* Custom bubble shape */}
                      {isUser ? (
                        <div className="absolute right-0 top-0 transform translate-x-2 translate-y-1 border-8 border-transparent border-l-[#005D4B]"></div>
                      ) : (
                        <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#222E35]"></div>
                      )}
                      
                      {/* Tool calls */}
                      {message.toolCalls && message.toolCalls.length > 0 && (
                        <div className="mt-2 text-xs">
                          {message.toolCalls.map((toolCall, index) => {
                            // Only show status messages for running tools
                            if (toolCall.status !== 'running') return null;
                            
                            // Get emoji and status text based on tool type
                            const toolEmojis: Record<string, string> = {
                              menu_info: "🍽️",
                              order_status: "⏱️",
                              menu_search: "🔍",
                              menu_highlights: "⭐",
                              location_info: "📍"
                            };
                            
                            const statusEmojis: Record<string, string> = {
                              running: "⏳",
                              complete: "✅",
                              error: "❌"
                            };
                            
                            const emoji = toolEmojis[toolCall.tool] || "🔍";
                            const statusEmoji = statusEmojis[toolCall.status] || "⏳";
                            
                            // Create WhatsApp-style action messages
                            const actionMessages: Record<string, Record<string, string>> = {
                              running: {
                                menu_info: `${emoji} *Checking our menu...* ${statusEmoji}`,
                                order_status: `${emoji} *Checking wait time...* ${statusEmoji}`,
                                menu_search: `${emoji} *Finding menu options...* ${statusEmoji}`,
                                menu_highlights: `${emoji} *Getting popular items...* ${statusEmoji}`,
                                location_info: `${emoji} *Checking our location...* ${statusEmoji}`
                              },
                              complete: {
                                menu_info: `${emoji} *Menu found!* ${statusEmoji}`,
                                order_status: `${emoji} *Wait time checked!* ${statusEmoji}`,
                                menu_search: `${emoji} *Options found!* ${statusEmoji}`,
                                menu_highlights: `${emoji} *Popular items found!* ${statusEmoji}`,
                                location_info: `${emoji} *Location found!* ${statusEmoji}`
                              },
                              error: {
                                menu_info: `${emoji} *Couldn't get menu* ${statusEmoji}`,
                                order_status: `${emoji} *Wait time unavailable* ${statusEmoji}`,
                                menu_search: `${emoji} *Couldn't find options* ${statusEmoji}`,
                                menu_highlights: `${emoji} *Popular items unavailable* ${statusEmoji}`,
                                location_info: `${emoji} *Location unavailable* ${statusEmoji}`
                              }
                            };
                            
                            const statusMessage = actionMessages[toolCall.status]?.[toolCall.tool] || 
                              `${emoji} *Working on your request...* ${statusEmoji}`;
                              
                            return (
                              <div key={index} className="mb-1">
                                <div className="italic text-[10px]">{statusMessage}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Typing indicator */}
              {isTyping && (
                <div className="flex justify-start">
                  <div className="relative max-w-[85%] rounded-lg p-2 bg-[#222E35] shadow-sm">
                    <div className="flex space-x-1 p-1">
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce delay-75"></div>
                      <div className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce delay-150"></div>
                    </div>
                    <div className="absolute left-0 top-0 transform -translate-x-2 translate-y-1 border-8 border-transparent border-r-[#222E35]"></div>
                  </div>
                </div>
              )}
              
              {/* We'll keep this ref but no longer need it for scrolling */}
              <div ref={messagesEndRef} />
            </div>
          </div>
          
          {/* Input area */}
          <div className="bg-[#1F2C34] p-2 flex items-center">
            <button className="p-1 text-[#8696A0]">
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM17 13H13V17H11V13H7V11H11V7H13V11H17V13Z" fill="currentColor"/>
              </svg>
            </button>
            <div className="flex-1 bg-[#2A3942] rounded-3xl mx-2 flex items-center px-3">
              <input
                ref={inputRef}
                type="text"
                placeholder="Message"
                className="w-full py-2 outline-none bg-transparent text-white text-xs"
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <button className="ml-2 text-[#8696A0]">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.5 11C16.3284 11 17 10.3284 17 9.5C17 8.67157 16.3284 8 15.5 8C14.6716 8 14 8.67157 14 9.5C14 10.3284 14.6716 11 15.5 11Z" fill="currentColor"/>
                  <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                  <path d="M15.5 11C16.3284 11 17 10.3284 17 9.5C17 8.67157 16.3284 8 15.5 8C14.6716 8 14 8.67157 14 9.5C14 10.3284 14.6716 11 15.5 11Z" fill="currentColor"/>
                  <path d="M8.5 11C9.32843 11 10 10.3284 10 9.5C10 8.67157 9.32843 8 8.5 8C7.67157 8 7 8.67157 7 9.5C7 10.3284 7.67157 11 8.5 11Z" fill="currentColor"/>
                  <path d="M12 17.5C14.33 17.5 16.31 16.04 17.11 14H6.89C7.69 16.04 9.67 17.5 12 17.5Z" fill="currentColor"/>
                  <path d="M11.99 2C6.47 2 2 6.48 2 12C2 17.52 6.47 22 11.99 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 11.99 2ZM12 20C7.58 20 4 16.42 4 12C4 7.58 7.58 4 12 4C16.42 4 20 7.58 20 12C20 16.42 16.42 20 12 20Z" fill="currentColor"/>
                </svg>
              </button>
            </div>
            <button 
              onClick={() => handleSendMessage()}
              disabled={inputValue.trim() === ''}
              className="p-1 text-[#00A884] disabled:opacity-50">
              {inputValue.trim() === '' ? (
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 15C10.34 15 9 13.66 9 12C9 10.34 10.34 9 12 9C13.66 9 15 10.34 15 12C15 13.66 13.66 15 12 15Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Home button / iPhone-style indicator */}
        <div className="mt-2 w-1/3 h-1 bg-gray-300 rounded-full mx-auto"></div>
      </div>
    </div>
  );
};

export default WhatsAppInterface;